import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import AuthLayout from '../layouts/AuthLayout.vue'
import ClientPortalAuthShell from '../layouts/ClientPortalAuthShell.vue'
import AppLayout from '../layouts/AppLayout.vue'
import ContractorLayout from '../layouts/ContractorLayout.vue'
import RouteViewComponent from '../layouts/RouterBypass.vue'
import {
  buildApiUrl,
  clearAuthToken,
  clearContractorToken,
  getAuthToken,
  getContractorToken,
  hasUsableAuthToken,
  valtFetch,
} from '@/utils/api'
import { isClientPortalHost, isContractorAppPath } from '@/utils/clientPortalHost'
import { getUserRole, syncUserRoleToStorage } from '@/utils/userRoleUtils'

const routes: RouteRecordRaw[] = [
  {
    path: '/:pathMatch(.*)*',
    redirect: () => {
      if (typeof window !== 'undefined' && isClientPortalHost()) {
        return { name: 'contractor-login' }
      }
      return { name: 'dashboard-v2' }
    },
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        beforeEnter: () => {
          if (isClientPortalHost()) return '/clients/login'
        },
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: () => (isClientPortalHost() ? '/clients/login' : { name: 'login' }),
      },
    ],
  },
  {
    path: '/contractor/login',
    redirect: '/clients/login',
  },
  {
    path: '/clients/login',
    component: ClientPortalAuthShell,
    children: [
      {
        name: 'contractor-login',
        path: '',
        component: () => import('../pages/contractor/ContractorLogin.vue'),
      },
    ],
  },
  {
    path: '/clients/recover-password',
    component: ClientPortalAuthShell,
    children: [
      {
        name: 'contractor-recover-password',
        path: '',
        component: () => import('../pages/contractor/ContractorRecoverPassword.vue'),
      },
    ],
  },
  {
    path: '/clients/recover-password-email',
    component: ClientPortalAuthShell,
    children: [
      {
        name: 'contractor-recover-password-email',
        path: '',
        component: () => import('../pages/contractor/ContractorRecoverPasswordEmail.vue'),
      },
    ],
  },
  {
    path: '/clients/change-password',
    component: ClientPortalAuthShell,
    children: [
      {
        name: 'contractor-change-password',
        path: '',
        component: () => import('../pages/contractor/ContractorChangePasswordPage.vue'),
      },
    ],
  },
  {
    path: '/contractor',
    component: ContractorLayout,
    meta: { requiresContractorAuth: true },
    children: [
      {
        name: 'contractor-dashboard',
        path: 'dashboard',
        component: () => import('../pages/contractor/ContractorDashboardPage.vue'),
      },
      {
        name: 'contractor-profile',
        path: 'profile',
        redirect: { name: 'contractor-preferences' },
      },
      {
        name: 'contractor-trips',
        path: 'trips',
        redirect: { name: 'contractor-dashboard' },
      },
      {
        name: 'contractor-alarms',
        path: 'alarms',
        redirect: { name: 'contractor-dashboard' },
      },
      {
        name: 'contractor-live-tracker',
        path: 'live-tracker',
        component: () => import('../pages/client-portal/LiveTrackerPage.vue'),
      },
      {
        name: 'contractor-preferences',
        path: 'preferences',
        component: () => import('../pages/contractor/ContractorPreferencesPage.vue'),
      },
      {
        name: 'contractor-vehicles',
        path: 'vehicles',
        component: () => import('../pages/contractor/ContractorVehiclesPage.vue'),
      },
      {
        name: 'contractor-drivers',
        path: 'drivers',
        component: () => import('../pages/contractor/ContractorDriversPage.vue'),
      },
      {
        name: 'contractor-billing',
        path: 'billing',
        redirect: { name: 'contractor-dashboard' },
      },
      {
        name: 'contractor-team',
        path: 'team',
        component: () => import('../pages/contractor/ContractorTeamPage.vue'),
      },
      {
        name: 'contractor-reports',
        path: 'reports',
        component: () => import('../pages/contractor/ClientReportsPage.vue'),
      },
      {
        name: 'contractor-odometer-reports',
        path: 'reports/odometer',
        component: () => import('../pages/contractor/ClientOdometerReportsPage.vue'),
      },
      {
        name: 'contractor-charging-reports',
        path: 'reports/charging',
        component: () => import('../pages/contractor/ClientChargingReportsPage.vue'),
      },
      {
        path: '',
        redirect: { name: 'contractor-dashboard' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
  {
    name: 'test-auth',
    path: '/test/auth',
    component: () => import('../pages/test/DummyAuthTest.vue'),
  },
  {
    name: 'test-role',
    path: '/test/role',
    component: () => import('../pages/test/RoleTestPage.vue'),
  },
  {
    path: '/',
    component: AppLayout,
    redirect: { name: 'dashboard-v2' },
    meta: { requiresAuth: true },
    children: [
      // Common routes (both roles)
      {
        name: 'dashboard',
        path: 'dashboard',
        redirect: { name: 'dashboard-v2' },
      },
      {
        name: 'dashboard-v2',
        path: 'dashboard-v2',
        component: () => import('../pages/admin/dashboard/NewDashboardPageV2.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'manager',
            'org_admin',
            'owner',
            'operator',
            'support_agent',
            'finance',
            'finance-staff',
          ],
        },
      },
      {
        name: 'dashboard-old',
        path: 'dashboard-old',
        component: () => import('../pages/admin/scorecard/Scorecard.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'manager',
            'org_admin',
            'owner',
            'operator',
            'support_agent',
            'finance',
            'finance-staff',
          ],
        },
      },
      {
        name: 'performance',
        path: 'performance',
        component: () => import('../pages/admin/performance/PerformanceInsightsPage.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'performance-old',
        path: 'performance-old',
        component: () => import('../pages/admin/performance/Scorecard.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'scorecard',
        path: 'scorecard',
        component: () => import('../pages/admin/performance/ScorecardNewPageV2.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'scorecard-v2',
        path: 'scorecard-v2',
        redirect: { name: 'scorecard' },
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'trips',
        path: 'trips',
        component: () => import('../pages/trips/TripsPage.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'profile',
        path: 'profile',
        component: () => import('../pages/profile/ProfilePage.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'driver-activity',
        path: 'driver-activity',
        component: () => import('../pages/driver-activity/DriverActivityDashboard.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'driver-activity-google',
        path: 'driver-activity-google',
        component: () => import('../pages/driver-activity/DriverActivityGoogleDashboard.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'demo-uber-region',
        path: 'demo/uber-region',
        component: () => import('../pages/demo/UberRegionDemo.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'faq',
        path: '/faq',
        component: () => import('../pages/faq/FaqPage.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'messages',
        path: '/messages',
        component: () => import('../pages/messages/MessagesPage.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'driver-training',
        path: '/driver-training',
        component: () => import('../pages/admin/driver-training/DriverTrainingPage.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'reports',
        path: '/reports',
        component: () => import('../pages/reports/Generator.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'odometer-reports',
        path: '/reports/odometer',
        component: () => import('../pages/reports/OdometerReportsPage.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'charging-reports',
        path: '/reports/charging',
        component: () => import('../pages/reports/ChargingReportsPage.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'vehicle-activity-reports',
        path: '/reports/vehicle-activity',
        component: () => import('../pages/reports/VehicleActivityReportsPage.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'fleet-telemetry-reports',
        path: '/reports/fleet-telemetry',
        component: () => import('../pages/reports/FleetTelemetryReportsPage.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
          ],
        },
      },
      {
        name: 'battery-swap-reports',
        path: '/reports/battery-swaps',
        component: () => import('../pages/reports/BatterySwapReportsPage.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
          ],
        },
      },
      {
        name: 'operator-dashboard',
        path: '/operator-dashboard/:id',
        component: () => import('../pages/operator-dashboard/Generator.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'analytics',
        path: 'analytics',
        component: () => import('../pages/analytics/AnalyticsDashboard.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'simple-dashboard',
        path: 'simple-dashboard',
        component: () => import('../pages/dashboard/SimpleDashboard.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'teams',
        path: 'teams',
        component: () => import('../pages/teams/TeamsPage.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'team-detail',
        path: 'teams/:id',
        component: () => import('../pages/teams/TeamDetailPage.vue'),
        props: true,
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'team-api-test',
        path: 'teams-api-test',
        component: () => import('../pages/teams/TeamApiTestPage.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'teams-debug',
        path: 'teams-debug',
        component: () => import('../pages/teams/TeamsDebugPage.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'teams-simple-test',
        path: 'teams-simple-test',
        component: () => import('../pages/teams/TeamsSimpleTestPage.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'operators',
        path: 'operators',
        component: () => import('../pages/operators/UsersPage.vue'),
        meta: { allowedRoles: ['admin', 'manager', 'org_admin', 'owner'] },
      },
      {
        name: 'tickets',
        path: 'tickets',
        component: () => import('../pages/tickets/TicketsPage.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },

      // Driver app support tickets (Firebase) — same cases as Flutter
      {
        name: 'driver-support',
        path: 'driver-support',
        component: () => import('../pages/support/SupportDeskPage.vue'),
        meta: {
          allowedRoles: ['admin', 'manager', 'org_admin', 'owner', 'support_agent', 'finance', 'recruiter'],
        },
      },
      {
        name: 'driver-support-manage',
        path: 'driver-support-manage',
        component: () => import('../pages/support/SupportManagePage.vue'),
        meta: {
          allowedRoles: ['admin', 'manager', 'org_admin', 'owner', 'team_manager'],
        },
      },
      {
        name: 'driver-support-ticket',
        path: 'driver-support/:ticketId',
        component: () => import('../pages/support/SupportTicketChatPage.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'manager',
            'org_admin',
            'owner',
            'support_agent',
            'finance',
            'recruiter',
            'team_manager',
          ],
        },
      },

      // Operator-specific routes
      {
        name: 'today',
        path: 'today',
        component: () => import('../pages/admin/live/Live.vue'),
        meta: {
          allowedRoles: [
            'operator',
            'admin',
            'manager',
            'org_admin',
            'owner',
            'recruiter',
            'finance',
            'finance-staff',
            'support_agent',
            'guest',
            'team_manager',
            'analyst',
            'developer',
            'driver',
          ],
        },
      },
      {
        name: 'compare',
        path: 'compare',
        component: () => import('../pages/admin/compare/Live.vue'),
        meta: { allowedRoles: ['operator', 'admin', 'manager', 'org_admin', 'owner', 'recruiter', 'support_agent'] },
      },
      {
        name: 'issues',
        path: 'issues',
        component: () => import('../pages/issues/IssuesPage.vue'),
        meta: { allowedRoles: ['operator', 'admin', 'manager', 'org_admin', 'owner', 'recruiter', 'support_agent'] },
      },
      {
        name: 'vehicles',
        path: 'uber/vehicles',
        component: () => import('../pages/vehicles/VehiclePage.vue'),
        meta: {
          allowedRoles: [
            'operator',
            'admin',
            'manager',
            'org_admin',
            'owner',
            'recruiter',
            'support_agent',
            'guest',
            'team_manager',
          ],
        },
      },
      {
        name: 'our-vehicles',
        path: 'our-vehicles',
        component: () => import('../pages/our-vehicles/OurVehiclesPage.vue'),
        meta: {
          allowedRoles: [
            'operator',
            'admin',
            'manager',
            'org_admin',
            'owner',
            'recruiter',
            'support_agent',
            'guest',
            'team_manager',
            'analyst',
            'finance',
            'finance-staff',
            'developer',
            'driver',
            'client_portal',
            'analyst_viewer_org',
          ],
        },
      },
      {
        name: 'our-vehicle-single',
        path: 'our-vehicles/single/:vehicleId',
        component: () => import('../pages/our-vehicles/OurVehicleSingleView.vue'),
        props: true,
        meta: {
          allowedRoles: [
            'operator',
            'admin',
            'manager',
            'org_admin',
            'owner',
            'recruiter',
            'support_agent',
            'guest',
            'team_manager',
            'analyst',
            'finance',
            'finance-staff',
            'developer',
            'driver',
            'client_portal',
            'analyst_viewer_org',
          ],
        },
      },
      {
        name: 'bikes',
        path: 'bikes',
        component: () => import('../pages/bikes/BikesPage.vue'),
        meta: {
          allowedRoles: [
            'operator',
            'admin',
            'manager',
            'org_admin',
            'owner',
            'recruiter',
            'support_agent',
            'guest',
            'team_manager',
            'analyst',
            'finance',
            'finance-staff',
            'developer',
            'driver',
            'client_portal',
            'analyst_viewer_org',
          ],
        },
      },
      {
        name: 'bike-single',
        path: 'bikes/single/:deviceId',
        component: () => import('../pages/bikes/BikeSingleView.vue'),
        props: true,
        meta: {
          allowedRoles: [
            'operator',
            'admin',
            'manager',
            'org_admin',
            'owner',
            'recruiter',
            'support_agent',
            'guest',
            'team_manager',
            'analyst',
            'finance',
            'finance-staff',
            'developer',
            'driver',
            'client_portal',
            'analyst_viewer_org',
          ],
        },
      },
      {
        name: 'go-metro-vehicles',
        path: 'go-metro-vehicles',
        redirect: (to) => ({ name: 'tracking-devices', query: to.query }),
      },
      {
        name: 'aeversa-vehicles',
        path: 'aeversa-vehicles',
        redirect: (to) => ({ name: 'our-vehicles', query: { ...to.query, filter: 'charging' } }),
      },
      {
        name: 'aeversa-vehicle-detail',
        path: 'aeversa-vehicles/:id',
        redirect: (to) => ({ name: 'our-vehicle-single', params: { vehicleId: String(to.params.id) } }),
      },
      {
        name: 'uteh-vehicles',
        path: 'uteh-vehicles',
        component: () => import('../pages/uteh-vehicles/UtehVehiclesPage.vue'),
        meta: {
          allowedRoles: [
            'operator',
            'admin',
            'manager',
            'org_admin',
            'owner',
            'recruiter',
            'support_agent',
            'guest',
            'team_manager',
            'analyst',
            'finance',
            'finance-staff',
            'developer',
            'driver',
            'client_portal',
            'analyst_viewer_org',
          ],
        },
      },
      {
        name: 'uteh-vehicle-detail',
        path: 'uteh-vehicles/:registration',
        component: () => import('../pages/uteh-vehicles/UtehVehicleDetailPage.vue'),
        props: true,
        meta: {
          allowedRoles: [
            'operator',
            'admin',
            'manager',
            'org_admin',
            'owner',
            'recruiter',
            'support_agent',
            'guest',
            'team_manager',
            'analyst',
            'finance',
            'finance-staff',
            'developer',
            'driver',
            'client_portal',
            'analyst_viewer_org',
          ],
        },
      },
      {
        name: 'alarms',
        path: 'alarms',
        component: () => import('../pages/alarms/AlarmPage.vue'),
        meta: { allowedRoles: ['operator', 'admin', 'manager', 'org_admin', 'owner', 'recruiter', 'support_agent'] },
      },
      {
        name: 'drivers',
        path: 'uber/drivers',
        component: () => import('../pages/drivers/DriverPage.vue'),
        meta: {
          allowedRoles: [
            'operator',
            'admin',
            'manager',
            'org_admin',
            'owner',
            'recruiter',
            'support_agent',
            'guest',
            'team_manager',
            'finance',
            'finance-staff',
          ],
        },
      },
      {
        name: 'our-drivers',
        path: 'our-drivers',
        component: () => import('../pages/our-drivers/OurDriversPage.vue'),
        meta: {
          allowedRoles: [
            'operator',
            'admin',
            'manager',
            'org_admin',
            'owner',
            'recruiter',
            'support_agent',
            'guest',
            'team_manager',
            'analyst',
            'finance',
            'finance-staff',
            'developer',
            'driver',
            'client_portal',
            'analyst_viewer_org',
          ],
        },
      },
      {
        name: 'our-driver-single',
        path: 'our-drivers/single/:driverId',
        component: () => import('../pages/our-drivers/OurDriverSingleView.vue'),
        props: true,
        meta: {
          allowedRoles: [
            'operator',
            'admin',
            'manager',
            'org_admin',
            'owner',
            'recruiter',
            'support_agent',
            'guest',
            'team_manager',
            'analyst',
            'finance',
            'finance-staff',
            'developer',
            'driver',
            'client_portal',
            'analyst_viewer_org',
          ],
        },
      },
      {
        name: 'maintenance',
        path: 'maintenance',
        component: () => import('../pages/maintainance/MaintainancePage.vue'),
        meta: { allowedRoles: ['operator', 'admin', 'manager', 'org_admin', 'owner', 'recruiter', 'support_agent'] },
      },
      {
        name: 'maintenance-items',
        path: 'maintenance/items',
        component: () => import('../pages/maintainance-item/MaintainanceItemPage.vue'),
        meta: { allowedRoles: ['operator', 'admin', 'manager', 'org_admin', 'owner', 'recruiter', 'support_agent'] },
      },
      {
        name: 'maintenance-workshops',
        path: 'maintenance/workshops',
        component: () => import('../pages/maintainance-workshop/MaintainanceWorkshopPage.vue'),
        meta: { allowedRoles: ['operator', 'admin', 'manager', 'org_admin', 'owner', 'recruiter', 'support_agent'] },
      },
      {
        name: 'driversingle',
        path: 'uber/driver/single/:carNumber',
        redirect: (to) => ({
          name: 'driver-new-single',
          params: { driverId: to.params.carNumber },
        }),
        meta: { allowedRoles: ['operator', 'admin', 'manager', 'org_admin', 'owner', 'recruiter', 'support_agent'] },
      },
      {
        name: 'driver-new-single',
        path: 'driver/new/single/:driverId',
        component: () => import('../pages/admin/driver-new-single/DriverNewSinglePage.vue'),
        props: true,
        meta: { allowedRoles: ['operator', 'admin', 'manager', 'org_admin', 'owner', 'recruiter', 'support_agent'] },
      },
      {
        name: 'ticketsingle',
        path: 'issues/single/:ticketNumber',
        component: () => import('../pages/admin/ticketsingle/Live.vue'),
        props: true,
        meta: { allowedRoles: ['operator', 'admin', 'manager', 'org_admin', 'owner', 'recruiter', 'support_agent'] },
      },
      {
        name: 'vehiclesingle',
        path: 'uber/vehicle/single/:carNumber',
        component: () => import('../pages/admin/vehiclesingle/Live.vue'),
        props: true,
        meta: { allowedRoles: ['operator', 'admin', 'manager', 'org_admin', 'owner', 'recruiter', 'support_agent'] },
      },
      {
        name: 'driver-onboarding',
        path: 'driver-onboarding',
        redirect: { name: 'recruiter-dashboard' },
        meta: { allowedRoles: ['admin', 'manager', 'org_admin', 'owner', 'recruiter'] },
      },
      {
        name: 'driver-training',
        path: 'driver-training',
        component: () => import('../pages/admin/driver-training/DriverTrainingPage.vue'),
        meta: { allowedRoles: ['admin', 'manager', 'org_admin', 'owner', 'recruiter'] },
      },
      {
        name: 'training-types',
        path: 'training-types',
        component: () => import('../pages/admin/driver-training/TrainingTypesPage.vue'),
        meta: { allowedRoles: ['admin', 'manager', 'org_admin', 'owner', 'recruiter'] },
      },
      {
        name: 'ev-dashboard',
        path: 'ev-dashboard',
        component: () => import('../pages/admin/ev-dashboard/EvDashboardPage.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'finance',
            'finance-staff',
          ],
        },
      },
      {
        name: 'charging-stations',
        path: 'ev-dashboard/charging-stations',
        component: () => import('../pages/admin/ev-dashboard/EvDashboardPage.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'finance',
            'finance-staff',
          ],
        },
      },
      {
        name: 'charging-stations-new',
        path: 'ev-dashboard/charging-stations-new',
        component: () => import('../pages/admin/ev-dashboard/ChargingStationsNewPage.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'finance',
            'finance-staff',
          ],
        },
      },
      {
        name: 'charging-sessions',
        path: 'ev-dashboard/charging-sessions',
        component: () => import('../pages/admin/ev-dashboard/ChargingSessionsPage.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'finance',
            'finance-staff',
          ],
        },
      },
      {
        name: 'charging-station-settings',
        path: 'ev-dashboard/charging-station-settings',
        component: () => import('../pages/admin/ev-dashboard/ChargingStationSettings.vue'),
        meta: {
          allowedRoles: ['admin', 'operator', 'manager', 'org_admin'],
        },
      },
      {
        name: 'finance-dashboard',
        path: 'finance-dashboard',
        component: () => import('../pages/admin/finance/FinancialDashboardPage.vue'),
        meta: { allowedRoles: ['admin', 'manager', 'org_admin', 'finance', 'finance-staff'] },
      },
      {
        name: 'finance-ledgers',
        path: 'finance-ledgers',
        component: () => import('../pages/finance/FinanceLedgersOverviewPage.vue'),
        meta: { allowedRoles: ['admin', 'manager', 'org_admin', 'finance', 'finance-staff'] },
      },
      {
        name: 'outbound-messages',
        path: 'outbound-messages',
        component: () => import('../pages/our-drivers/OutboundMessagesPage.vue'),
        meta: {
          allowedRoles: ['admin', 'manager', 'org_admin', 'finance', 'finance-staff', 'support_agent', 'team_manager'],
        },
      },
      {
        name: 'outbound-chat',
        path: 'outbound-messages/:driverId',
        component: () => import('../pages/our-drivers/OutboundChatPage.vue'),
        props: true,
        meta: {
          allowedRoles: ['admin', 'manager', 'org_admin', 'finance', 'finance-staff', 'support_agent', 'team_manager'],
        },
      },
      {
        name: 'immobiliser',
        path: 'immobiliser',
        component: () => import('../pages/immobiliser/ImmobiliserAssetsPage.vue'),
        meta: { allowedRoles: ['admin', 'manager', 'org_admin'] },
      },
      {
        name: 'immobiliser-vehicle',
        path: 'immobiliser/vehicle/:vehicleId',
        component: () => import('../pages/immobiliser/ImmobiliserVehiclePage.vue'),
        props: true,
        meta: { allowedRoles: ['admin', 'manager', 'org_admin'] },
      },
      {
        name: 'immobiliser-history-detail',
        path: 'immobiliser/history/:id',
        component: () => import('../pages/immobiliser/ImmobiliserHistoryDetailPage.vue'),
        props: true,
        meta: { allowedRoles: ['admin', 'manager', 'org_admin'] },
      },
      {
        name: 'role-management',
        path: 'role-management',
        component: () => import('../pages/admin/role-management/RoleManagementPage.vue'),
        meta: { allowedRoles: ['admin', 'org_admin'] },
      },
      {
        name: 'ticket-management',
        path: 'ticket-management',
        component: () => import('../pages/admin/ticket-management/TicketManagementPage.vue'),
        meta: { allowedRoles: ['admin', 'manager', 'org_admin', 'owner'] },
      },
      {
        name: 'user-management',
        path: 'user-management',
        component: () => import('../pages/admin/user-management/UserManagementPage.vue'),
        meta: { allowedRoles: ['admin', 'manager', 'org_admin', 'owner'] },
      },
      {
        name: 'user-monitoring',
        path: 'user-monitoring',
        component: () => import('../pages/admin/user-monitoring/UserMonitoringPage.vue'),
        meta: { allowedRoles: ['admin', 'manager', 'org_admin', 'owner'] },
      },
      {
        name: 'faq-management',
        path: 'faq-management',
        component: () => import('../pages/admin/faq-management/FAQManagementPage.vue'),
        meta: { allowedRoles: ['admin', 'manager', 'org_admin', 'owner'] },
      },
      {
        name: 'admin-billing',
        path: 'billing',
        component: () => import('../pages/admin/billing-packages/BillingPackagesPage.vue'),
        meta: { allowedRoles: ['admin', 'manager', 'org_admin', 'owner'] },
      },
      {
        name: 'docs',
        path: 'docs',
        component: () => import('../pages/docs/DocsPage.vue'),
        meta: { allowedRoles: ['admin', 'manager', 'org_admin'] },
      },
      {
        name: 'docs-system',
        path: 'docs/system',
        component: () => import('../pages/docs/SystemPage.vue'),
        meta: { allowedRoles: ['admin', 'manager', 'org_admin'] },
      },
      {
        name: 'docs-api',
        path: 'docs/api',
        component: () => import('../pages/docs/ApiDocsPage.vue'),
        meta: { allowedRoles: ['admin', 'manager', 'org_admin'] },
      },
      {
        name: 'docs-playground',
        path: 'docs/playground',
        component: () => import('../pages/docs/ApiPlaygroundPage.vue'),
        meta: { allowedRoles: ['admin', 'manager', 'org_admin'] },
      },
      {
        name: 'push-notifications',
        path: 'push-notifications',
        component: () => import('../pages/admin/push-notifications/PushNotificationsPage.vue'),
        meta: { allowedRoles: ['admin', 'manager', 'org_admin'] },
      },
      {
        name: 'contractor-management',
        path: 'contractor-management',
        component: () => import('../pages/admin/contractor-management/ContractorManagementPage.vue'),
        meta: { allowedRoles: ['admin', 'manager', 'org_admin'] },
      },
      {
        name: 'bulk-upload-assets',
        path: 'bulk-upload-assets',
        component: () => import('../pages/admin/bulk-upload-assets/BulkUploadAssetsPage.vue'),
        meta: { allowedRoles: ['admin', 'manager', 'org_admin'] },
      },
      {
        name: 'client-detail',
        path: 'contractor-management/:id',
        component: () => import('../pages/admin/contractor-management/ClientDetailPage.vue'),
        meta: { allowedRoles: ['admin', 'manager', 'org_admin'] },
      },
      {
        name: 'kpi-management',
        path: 'kpi-management',
        component: () => import('../pages/admin/kpi-management/KpiManagementPage.vue'),
        meta: { allowedRoles: ['admin', 'manager', 'org_admin'] },
      },
      {
        name: 'dashboard-widgets',
        path: 'dashboard-widgets',
        component: () => import('../pages/admin/dashboard-widgets/DashboardWidgetsPage.vue'),
        meta: { allowedRoles: ['admin', 'manager', 'org_admin', 'owner'] },
      },
      {
        name: 'violations-escalation',
        path: 'violations-escalation',
        component: () => import('../pages/admin/violations-escalation/ViolationsEscalationPage.vue'),
        meta: { allowedRoles: ['admin', 'manager', 'org_admin', 'owner', 'operator', 'support_agent', 'finance'] },
      },
      {
        name: 'recruiter-dashboard',
        path: 'recruiter-dashboard',
        component: () => import('../pages/recruiter/RecruiterDashboard.vue'),
        meta: { allowedRoles: ['recruiter', 'admin', 'org_admin', 'owner'] },
      },
      {
        name: 'applicants',
        path: 'applicants',
        component: () => import('../pages/recruiter/ApplicantManagement.vue'),
        meta: { allowedRoles: ['recruiter', 'admin', 'org_admin', 'owner'] },
      },
      {
        name: 'documents-pending',
        path: 'documents-pending',
        component: () => import('../pages/recruiter/DocumentReview.vue'),
        meta: { allowedRoles: ['recruiter', 'admin', 'org_admin', 'owner'] },
      },
      {
        name: 'training-schedule',
        path: 'training-schedule',
        component: () => import('../pages/recruiter/TrainingManagement.vue'),
        meta: { allowedRoles: ['recruiter', 'admin', 'org_admin', 'owner'] },
      },
      {
        name: 'applicant-pending',
        path: 'applicants/pending',
        component: () => import('../pages/recruiter/PendingApplicantsPage.vue'),
        meta: { allowedRoles: ['recruiter', 'admin', 'org_admin', 'owner'] },
      },
      {
        name: 'documents-reviewed',
        path: 'applicants/documents/reviewed',
        component: () => import('../pages/recruiter/DocumentsReviewedPage.vue'),
        meta: { allowedRoles: ['recruiter', 'admin', 'org_admin', 'owner'] },
      },
      {
        name: 'background-checks',
        path: 'background-checks',
        component: () => import('../pages/recruiter/BackgroundChecksPage.vue'),
        meta: { allowedRoles: ['recruiter', 'admin', 'org_admin', 'owner'] },
      },
      {
        name: 'contract-upload',
        path: 'contract-upload',
        component: () => import('../pages/recruiter/ContractUploadPage.vue'),
        meta: { allowedRoles: ['recruiter', 'admin', 'org_admin', 'owner'] },
      },
      {
        name: 'profile-pictures',
        path: 'profile-pictures',
        component: () => import('../pages/recruiter/ProfilePicturesPage.vue'),
        meta: { allowedRoles: ['recruiter', 'admin', 'org_admin', 'owner'] },
      },
      {
        name: 'training-slots',
        path: 'training-slots',
        component: () => import('../pages/recruiter/TrainingSlotsPage.vue'),
        meta: { allowedRoles: ['recruiter', 'admin', 'org_admin', 'owner'] },
      },
      {
        name: 'applicant-booked',
        path: 'applicants/booked',
        component: () => import('../pages/drivers/RecruitmentDriverPage.vue'),
        meta: { allowedRoles: ['recruiter', 'admin', 'org_admin', 'owner'] },
      },
      {
        name: 'applicant-approved',
        path: 'applicants/approved',
        component: () => import('../pages/drivers/RecruitmentDriverPage.vue'),
        meta: { allowedRoles: ['recruiter', 'admin', 'org_admin', 'owner'] },
      },
      {
        name: 'applicant-accepted',
        path: 'applicants/accepted',
        component: () => import('../pages/recruiter/AcceptedApplicantsPage.vue'),
        meta: { allowedRoles: ['recruiter', 'admin', 'org_admin', 'owner'] },
      },
      {
        name: 'applicant-active',
        path: 'applicants/active',
        component: () => import('../pages/recruiter/ActiveDriversPage.vue'),
        meta: { allowedRoles: ['recruiter', 'admin', 'org_admin', 'owner'] },
      },
      {
        name: 'applicant-rejected',
        path: 'applicants/rejected',
        component: () => import('../pages/recruiter/RejectedApplicantsPage.vue'),
        meta: { allowedRoles: ['recruiter', 'admin', 'org_admin', 'owner'] },
      },
      {
        name: 'training-schedule',
        path: 'training/schedule',
        component: () => import('../pages/admin/driver-training/DriverTrainingPage.vue'),
        meta: { allowedRoles: ['recruiter', 'admin', 'org_admin', 'owner'] },
      },
      {
        name: 'training-booking',
        path: 'training/booking',
        component: () => import('../pages/admin/driver-training/DriverTrainingPage.vue'),
        meta: { allowedRoles: ['recruiter', 'admin', 'org_admin', 'owner'] },
      },
      {
        name: 'applicant-detail',
        path: 'applicants/:id',
        component: () => import('../pages/drivers/ApplicantDetailPage.vue'),
        meta: { allowedRoles: ['recruiter', 'admin', 'org_admin', 'owner'] },
        props: true,
      },
      {
        name: 'applicant-detail-by-driver-id',
        path: 'applicants/driver/:driverId',
        component: () => import('../pages/drivers/ApplicantDetailPage.vue'),
        meta: { allowedRoles: ['recruiter', 'admin', 'org_admin', 'owner'] },
        props: true,
      },

      // Admin and Manager-specific routes
      {
        name: 'users',
        path: 'users',
        component: () => import('../pages/users/UsersPage.vue'),
        meta: { allowedRoles: ['admin', 'manager', 'org_admin', 'owner'] },
      },
      {
        name: 'monitor',
        path: 'monitor',
        component: () => import('../pages/admin/monitor/Live.vue'),
        meta: {
          allowedRoles: [
            'admin',
            'operator',
            'manager',
            'org_admin',
            'support_agent',
            'guest',
            'team_manager',
            'owner',
            'analyst',
            'finance',
            'developer',
            'driver',
            'recruiter',
          ],
        },
      },
      {
        name: 'scorecard-old',
        path: 'scorecard-old',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
        meta: { allowedRoles: ['admin', 'manager', 'org_admin', 'owner', 'finance', 'finance-staff'] },
      },
      {
        name: 'payments',
        path: '/payments',
        component: RouteViewComponent,
        meta: { allowedRoles: ['admin', 'manager', 'org_admin', 'finance', 'finance-staff'] },
        children: [
          {
            name: 'payment-methods',
            path: 'payment-methods',
            component: () => import('../pages/payments/PaymentsPage.vue'),
          },
          {
            name: 'billing',
            path: 'billing',
            component: () => import('../pages/billing/BillingPage.vue'),
          },
          {
            name: 'pricing-plans',
            path: 'pricing-plans',
            component: () => import('../pages/pricing-plans/PricingPlans.vue'),
          },
          {
            name: 'financial-upload',
            path: 'financial-upload',
            component: () => import('../pages/finance/FinancialSheetUploadPage.vue'),
            meta: { allowedRoles: ['admin', 'manager', 'org_admin', 'finance', 'finance-staff'] },
          },
          {
            name: 'payouts',
            path: 'payouts',
            component: () => import('../pages/finance/PayoutsPage.vue'),
            meta: { allowedRoles: ['admin', 'manager', 'org_admin', 'finance', 'finance-staff'] },
          },
          {
            name: 'invoices',
            path: 'invoices',
            component: () => import('../pages/finance/InvoicesPage.vue'),
            meta: { allowedRoles: ['admin', 'manager', 'org_admin', 'finance', 'finance-staff'] },
          },
        ],
      },
      {
        name: 'valt-v2-valt-drivers',
        path: 'valt-v2/valt/drivers',
        redirect: (to) => ({ name: 'our-drivers', query: to.query }),
      },
      {
        name: 'valt-v2-uber-drivers',
        path: '3/valt-v2/uber/drivers',
        component: () => import('../pages/drivers/DriverPage.vue'),
        meta: {
          allowedRoles: [
            'operator',
            'admin',
            'manager',
            'org_admin',
            'owner',
            'recruiter',
            'support_agent',
            'guest',
            'team_manager',
            'finance',
            'finance-staff',
          ],
        },
      },
      {
        name: 'uber-deployed-drivers',
        path: 'uber/deployed/drivers',
        redirect: (to) => ({ name: 'our-drivers', query: { ...to.query, status: 'active' } }),
      },
      {
        name: 'valt-v2-valt-vehicles',
        path: 'valt-v2/valt/vehicles',
        redirect: (to) => ({ name: 'our-vehicles', query: to.query }),
      },
      {
        name: 'valt-v2-uber-vehicles',
        path: 'valt-v2/uber/vehicles',
        component: () => import('../pages/vehicles/VehiclePage.vue'),
        meta: {
          allowedRoles: [
            'operator',
            'admin',
            'manager',
            'org_admin',
            'owner',
            'recruiter',
            'support_agent',
            'guest',
            'team_manager',
          ],
        },
      },
      {
        name: 'vehicle-mapping',
        path: 'vehicle-mapping',
        component: () => import('../pages/vehicle-mapping/VehicleMappingPage.vue'),
        meta: {
          allowedRoles: ['operator', 'admin', 'manager', 'org_admin', 'owner', 'support_agent'],
        },
      },
      {
        name: 'driver-matching',
        path: 'driver-matching',
        component: () => import('../pages/driver-matching/DriverMatchingPage.vue'),
        meta: {
          allowedRoles: ['operator', 'admin', 'manager', 'org_admin', 'owner', 'recruiter', 'support_agent'],
        },
      },
      {
        name: 'tracking-devices',
        path: 'tracking-devices',
        component: () => import('../pages/tracking-devices/TrackingDevicesPage.vue'),
        meta: {
          allowedRoles: ['operator', 'admin', 'manager', 'org_admin', 'owner', 'support_agent', 'developer'],
        },
      },
    ],
  },
]

let lastValidatedToken: string | null = null

const validateAuthToken = async (token: string | null): Promise<boolean> => {
  if (!hasUsableAuthToken(token)) {
    return false
  }

  try {
    const response = await valtFetch(buildApiUrl('/users/logged'), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      return false
    }

    const userData = await response.json().catch(() => null)
    const isValidUserPayload = Boolean(userData && (userData.id ?? null) !== null && typeof userData.role === 'string')

    if (!isValidUserPayload) {
      return false
    }

    if (userData) {
      localStorage.setItem('userData', JSON.stringify(userData))
      syncUserRoleToStorage(userData)
      window.dispatchEvent(new CustomEvent('userDataUpdated'))
    }

    return true
  } catch (error) {
    console.error('Error validating auth token on navigation:', error)
    return false
  }
}

const clearAuthStorage = () => {
  clearAuthToken()
  clearContractorToken()
  void import('@/services/driverSupportFirestoreService').then(async ({ driverSupportFirestoreService }) => {
    await driverSupportFirestoreService.signOffSupportAgent()
    localStorage.removeItem('userData')
    localStorage.removeItem('userRole')
    localStorage.removeItem('region')
    const { clearStaffSession } = await import('@/firebase')
    await clearStaffSession()
  })
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.hash) return { el: to.hash, behavior: 'smooth' }
    return { top: 0 }
  },
})

router.beforeEach(async (to, from, next) => {
  const contractorToken = getContractorToken()
  const mustChangePassword =
    typeof window !== 'undefined' && localStorage.getItem('contractor_must_change_password') === 'true'
  const isContractorChangePassword = to.name === 'contractor-change-password'

  // Client-portal-only hosts (e.g. yassir.valt.africa): no admin console routes.
  if (isClientPortalHost()) {
    const onContractorApp = isContractorAppPath(to.path)

    if (to.path.startsWith('/auth') || to.name === 'login') {
      if (contractorToken && mustChangePassword) {
        next({ name: 'contractor-change-password' })
        return
      }
      next(contractorToken ? { name: 'contractor-dashboard' } : { name: 'contractor-login' })
      return
    }

    if (!onContractorApp) {
      if (contractorToken && mustChangePassword) {
        next({ name: 'contractor-change-password' })
        return
      }
      next(contractorToken ? { name: 'contractor-dashboard' } : { name: 'contractor-login' })
      return
    }
  }

  const requiresContractorAuth = to.matched.some((r) => r.meta.requiresContractorAuth)
  const isContractorLogin = to.name === 'contractor-login'

  if (isContractorChangePassword) {
    if (!contractorToken) {
      next({ name: 'contractor-login' })
      return
    }
    next()
    return
  }

  if (requiresContractorAuth && !contractorToken) {
    next({ name: 'contractor-login' })
    return
  }
  if (contractorToken && mustChangePassword && (requiresContractorAuth || isContractorAppPath(to.path))) {
    next({ name: 'contractor-change-password' })
    return
  }
  if (requiresContractorAuth && contractorToken) {
    const { useContractorAuthStore } = await import('@/stores/contractorAuthStore')
    const contractorAuth = useContractorAuthStore()
    contractorAuth.restoreFromStorage()
  }
  if (isContractorLogin && contractorToken) {
    if (mustChangePassword) {
      next({ name: 'contractor-change-password' })
      return
    }
    next({ name: 'contractor-dashboard' })
    return
  }

  const token = getAuthToken()
  const userRole = getUserRole() as
    | 'owner'
    | 'org_admin'
    | 'team_manager'
    | 'support_agent'
    | 'analyst'
    | 'finance'
    | 'developer'
    | 'client_portal'
    | 'driver'
    | 'recruiter'
    | 'admin'
    | 'operator'
    | 'manager'
    | 'guest'

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
  const allowedRoles: string[] = []
  to.matched.forEach((record) => {
    if (record.meta.allowedRoles && Array.isArray(record.meta.allowedRoles)) {
      allowedRoles.push(...record.meta.allowedRoles)
    }
  })

  // Remove duplicate roles
  const uniqueAllowedRoles = [...new Set(allowedRoles)]

  // Special handling for client portal users: rely on contractor_token only,
  // and skip console token validation to avoid unwanted logouts.
  if (userRole === 'client_portal') {
    if (requiresAuth) {
      if (!contractorToken) {
        next({ name: 'contractor-login' })
        return
      }

      const effectiveRole = uniqueAllowedRoles.includes('admin') ? 'admin' : 'client_portal'
      if (uniqueAllowedRoles.length === 0 || uniqueAllowedRoles.includes(effectiveRole)) {
        next()
      } else {
        next({ name: '404' })
      }
    } else {
      next()
    }
    return
  }

  // Default console / admin auth flow
  if (requiresAuth) {
    if (!token) {
      clearAuthStorage()
      next({ name: 'login' })
      return
    }

    if (!hasUsableAuthToken(token)) {
      clearAuthStorage()
      next({ name: 'login' })
      return
    }

    if (lastValidatedToken !== token) {
      const isValid = await validateAuthToken(token)

      if (!isValid) {
        clearAuthStorage()
        next({ name: 'login' })
        return
      }

      lastValidatedToken = token
    }
  }

  if (requiresAuth && token) {
    if (uniqueAllowedRoles.length === 0 || uniqueAllowedRoles.includes(userRole)) {
      next()
    } else {
      next({ name: '404' })
    }
  } else {
    next()
  }
})

export default router
